import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { SearchBar } from "@/components/SearchBar";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { BUSINESS_INFO } from "@/constants/seo";
import { supabase } from "@/integrations/supabase/client";
import { CitableStats } from "@/components/seo/CitableStats";

// Enhanced homepage components with imagery
import {
  HeroWithImagery,
  CategoryShowcase,
  WhyChooseUs,
  ProfessionalCTA,
  ClosingCTA,
  FunnelCTASection
} from "@/components/homepage";

import { FeaturedCarousel } from "@/components/FeaturedCarousel";
import { useFeaturedCarousel } from "@/hooks/useFeaturedCarousel";
import { TestimonialsSection } from "@/components/testimonials/TestimonialsSection";
import {
  StickyBottomCTA,
  LiveActivityBar,
  TrustBadgesStrip,
  UrgencySection
} from "@/components/conversion";

const Index = () => {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState<string>("all");
  const [selectedCity, setSelectedCity] = useState<string>("all");
  const [countries, setCountries] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const { featuredCarousel, loading: loadingCarousel } = useFeaturedCarousel();

  // Country code to name mapping - memoized to prevent recreation
  const countryCodeToName = useMemo<Record<string, string>>(() => ({
    "ES": "España",
    "US": "Estados Unidos",
    "AR": "Argentina",
    "CL": "Chile",
    "PE": "Perú",
    "CO": "Colombia",
    "MX": "México",
    "VE": "Venezuela",
    "PA": "Panamá",
    "EC": "Ecuador",
    "AW": "Aruba",
    "TT": "Trinidad y Tobago",
    "FR": "Francia",
    "GB": "Reino Unido",
  }), []);

  // Fetch countries from cities table
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const { data, error } = await supabase
          .from("cities")
          .select("country")
          .eq("is_active", true);

        if (error) throw error;

        const uniqueCountryCodes = Array.from(new Set(data?.map(item => item.country).filter(Boolean))) as string[];
        const countryNames = uniqueCountryCodes
          .map(code => countryCodeToName[code] || code)
          .sort();
        setCountries(countryNames);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCountries();
  }, []);

  // Update cities when country changes
  useEffect(() => {
    const fetchCities = async () => {
      try {
        let query = supabase
          .from("cities")
          .select("name, slug, country")
          .eq("is_active", true);

        if (selectedCountry !== "all") {
          const countryCode = Object.entries(countryCodeToName).find(
            ([code, name]) => name === selectedCountry
          )?.[0];
          
          if (countryCode) {
            query = query.eq("country", countryCode);
          }
        }

        const { data, error } = await query.order("name");

        if (error) throw error;

        const cityNames = Array.from(
          new Set((data || []).map(item => item.name).filter(Boolean))
        ).sort() as string[];
        setCities(cityNames);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };

    fetchCities();
    setSelectedCity("all");
  }, [selectedCountry]);

  // Memoize structured data to prevent recreation on each render
  const structuredData = useMemo(() => [
    BUSINESS_INFO,
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Ekselencia",
      url: "https://ekselencia.com",
      description: "Ecosistema de entrenadores personales verificados. Encuentra tu entrenador ideal cerca de ti, validado por tu comunidad local.",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://ekselencia.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Servicios de Entrenamiento Personal",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Entrenadores Personales", item: "https://ekselencia.com/trainers" },
        { "@type": "ListItem", position: 2, name: "Cerca de Mí", item: "https://ekselencia.com/cerca-de-mi" },
        { "@type": "ListItem", position: 3, name: "Eventos Deportivos", item: "https://ekselencia.com/events" },
        { "@type": "ListItem", position: 4, name: "Gimnasios y Centros", item: "https://ekselencia.com/directory" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "¿Cómo encuentro un entrenador personal cerca de mí?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "En Ekselencia puedes filtrar entrenadores por ubicación, especialidad y valoraciones reales de clientes de tu zona para encontrar el profesional ideal.",
          },
        },
        {
          "@type": "Question",
          name: "¿Los entrenadores están verificados?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Sí, todos los entrenadores pasan por un proceso de verificación de credenciales profesionales y sus valoraciones provienen de clientes reales de la comunidad.",
          },
        },
        {
          "@type": "Question",
          name: "¿Cómo me registro como entrenador personal?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Crea tu perfil profesional gratuito, verifica tus credenciales y empieza a recibir contactos directos de clientes de tu ciudad. Sin comisiones por sesión.",
          },
        },
      ],
    },
  ], []);

  return (
    <div className="min-h-screen bg-background pt-[88px] lg:pt-[120px]">
      <SEO
        title="Entrenadores Personales Cerca de Ti | Ekselencia"
        description="Encuentra tu entrenador personal ideal verificado y cerca de ti. +800 entrenadores en 157 ciudades, validados por tu comunidad local."
        keywords={[
          "entrenador personal cerca de mi",
          "personal trainer madrid",
          "entrenador personal barcelona",
          "entrenador personal a domicilio",
          "buscar entrenador personal",
          "entrenadores verificados",
          "personal trainer online",
          "entrenamiento personalizado",
          "coach fitness",
          "preparador físico",
        ]}
        structuredData={structuredData}
      />
      {/* LiveActivityBar is fixed positioned - no need to render it here in document flow */}
      <LiveActivityBar />
      
      <Navigation />
      
      {/* 1. Hero Section with Imagery - full bleed behind nav */}
      <HeroWithImagery />

      {/* 2. Professional CTA Section - Right after hero */}
      <ProfessionalCTA />

      {/* 3. Category Showcase with Images */}
      <CategoryShowcase />

      {/* Trust Badges Strip */}
      <TrustBadgesStrip />

      {/* Citable Stats for AI engines */}
      <CitableStats className="py-8 bg-muted/20" />

      {/* 4. Why Choose Us with Stats */}
      <WhyChooseUs />

      {/* 5. Quick Trainer Search */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Busca Entrenadores en Tu Zona</h2>
              <p className="text-muted-foreground">Filtra por ciudad y especialidad para encontrar tu match perfecto</p>
            </div>
            
            <Card className="border-2 border-primary/20 shadow-lg">
              <CardContent className="p-6 md:p-8">
                <div className="mb-6">
                  <SearchBar className="w-full" />
                </div>

                <div className="pt-6 border-t">
                  <p className="text-sm text-muted-foreground mb-4 text-center">
                    O filtra por ubicación:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                      <SelectTrigger aria-label="Seleccionar país">
                        <SelectValue placeholder="País" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">🌍 Todos los países</SelectItem>
                        {countries.map((country) => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedCity} onValueChange={setSelectedCity}>
                      <SelectTrigger aria-label="Seleccionar ciudad">
                        <SelectValue placeholder="Ciudad" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">📍 Todas las ciudades</SelectItem>
                        {cities.map((city) => (
                          <SelectItem key={city} value={city}>
                            {city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    className="w-full bg-gradient-primary hover:opacity-90 py-6 text-lg"
                    onClick={() => {
                      const params = new URLSearchParams();
                      if (selectedCountry !== "all") params.set("country", selectedCountry);
                      if (selectedCity !== "all") params.set("city", selectedCity);
                      navigate(`/trainers?${params.toString()}`);
                    }}
                  >
                    <User className="w-5 h-5 mr-2" />
                    Buscar Entrenadores
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 6. Featured Carousel */}
      {!loadingCarousel && featuredCarousel.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Profesionales y Centros <span className="gradient-text">Destacados</span>
              </h2>
              <p className="text-muted-foreground">Los mejor valorados por la comunidad</p>
            </div>
            <FeaturedCarousel items={featuredCarousel} />
          </div>
        </section>
      )}

      {/* 7. Testimonials */}
      <TestimonialsSection />

      {/* Urgency Section - Limited Spots */}
      <UrgencySection />

      {/* 8. Funnel CTA Section */}
      <FunnelCTASection />

      {/* 9. Closing CTA */}
      <ClosingCTA />

      <Footer />
      
      {/* Sticky Bottom CTA - Appears after scrolling */}
      <StickyBottomCTA />
    </div>
  );
};

export default Index;
